// GoalSettingModal.js - Component for setting savings goals
import React, { useState } from 'react';
import { Target, X, DollarSign } from 'lucide-react';
import { Button } from '../components/ui';

export const GoalSettingModal = ({ 
  isOpen, 
  onClose, 
  child, 
  currentGoal = 100,
  onGoalSet 
}) => {
  const [goalAmount, setGoalAmount] = useState(currentGoal.toString());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const API_BASE_URL = 'https://growthsprouts-server.vercel.app/api';

  if (!isOpen || !child) return null;

  const handleSubmit = async () => {
    const amount = parseFloat(goalAmount);
    
    // Validation
    if (isNaN(amount) || amount <= 0) {
      setError('Please enter a valid goal amount');
      return;
    }
    
    if (amount < 10) {
      setError('Goal must be at least $10');
      return;
    }
    
    if (amount > 10000) {
      setError('Goal cannot exceed $10,000');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/children/${child.id}/goal`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          savings_goal: amount
        }),
      });

      const result = await response.json();

      if (result.success) {
        onGoalSet(amount);
        onClose();
      } else {
        setError(result.error || 'Failed to set goal');
      }
    } catch (error) {
      console.error('Error setting goal:', error);
      setError('Failed to set goal. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setGoalAmount(currentGoal.toString());
      setError('');
      onClose();
    }
  };

  const suggestedGoals = [25, 50, 100, 250, 500, 1000];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
              <Target className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Set Savings Goal</h3>
              <p className="text-sm text-gray-500">For {child.first_name || child.firstName}'s garden</p>
            </div>
          </div>
          <button 
            onClick={handleClose}
            disabled={isSubmitting}
            className="text-gray-400 hover:text-gray-600 p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Current Progress */}
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Current Progress</span>
              <span className="text-sm font-medium text-gray-900">
                ${(child.total_saved || 0).toFixed(2)} / ${currentGoal.toFixed(2)}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                style={{ 
                  width: `${Math.min(((child.total_saved || 0) / currentGoal) * 100, 100)}%` 
                }}
              />
            </div>
          </div>

          {/* Goal Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              New Savings Goal
            </label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="number"
                value={goalAmount}
                onChange={(e) => setGoalAmount(e.target.value)}
                placeholder="Enter goal amount"
                min="10"
                max="10000"
                step="5"
                disabled={isSubmitting}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            {error && (
              <p className="text-red-600 text-sm mt-1">{error}</p>
            )}
          </div>

          {/* Suggested Goals */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-3">Quick Select</p>
            <div className="grid grid-cols-3 gap-2">
              {suggestedGoals.map((amount) => (
                <button
                  key={amount}
                  onClick={() => setGoalAmount(amount.toString())}
                  disabled={isSubmitting}
                  className={`p-3 rounded-lg border text-sm font-medium transition-colors ${
                    goalAmount === amount.toString()
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 hover:border-gray-300 text-gray-700'
                  }`}
                >
                  ${amount}
                </button>
              ))}
            </div>
          </div>

          {/* Goal Benefits */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <div className="w-5 h-5 text-blue-600 mt-0.5">💡</div>
              <div>
                <p className="text-blue-800 font-medium text-sm mb-1">Why set a goal?</p>
                <ul className="text-blue-700 text-xs space-y-1">
                  <li>• Track progress and stay motivated</li>
                  <li>• Learn about planning and patience</li>
                  <li>• Celebrate achievements along the way</li>
                  <li>• Build healthy money habits early</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 p-6 border-t border-gray-100">
          <div className="flex space-x-3">
            <Button
              variant="ghost"
              onClick={handleClose}
              disabled={isSubmitting}
              className="flex-1 rounded-xl border border-gray-200 hover:bg-gray-50"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || !goalAmount}
              className="flex-1 rounded-xl bg-blue-500 hover:bg-blue-600 text-white"
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Setting Goal...
                </div>
              ) : (
                <>
                  <Target className="w-4 h-4 mr-2" />
                  Set Goal
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};